/**
 * Small per-device preferences (text size, layout choices) in localStorage — kept even
 * when the storage is full.
 *
 * localStorage holds about 5 MB per site, and a half-finished generate wizard keeps its
 * uploaded roster PDF in it (see wizardDraft) — well over a megabyte, one per session it
 * was opened for. Two or three of those fill it, and from then on every preference write
 * failed silently: the text size reset to "Fit all" on every reload. So a write that does
 * not fit first clears out stale and other sessions' drafts and tries again, and as a
 * last resort keeps the value for this tab (sessionStorage) so a reload still has it.
 */
import { freeDraftSpace } from './wizardDraft';

export function loadPref(key: string): string | null {
  try {
    const value = localStorage.getItem(key);
    if (value !== null) return value;
  } catch { /* storage blocked: fall through to this tab's copy */ }
  try { return sessionStorage.getItem(key); } catch { return null; }
}

export function savePref(key: string, value: string): void {
  const write = () => {
    localStorage.setItem(key, value);
    try { sessionStorage.removeItem(key); } catch { /* nothing to drop */ }
  };
  try { write(); return; } catch { /* full, most likely */ }
  try {
    while (freeDraftSpace()) {
      try { write(); return; } catch { /* still full: free more */ }
    }
  } catch { /* storage blocked altogether */ }
  try { sessionStorage.setItem(key, value); } catch { /* not remembered: still applied */ }
}
